"use client"
import { useState, useEffect } from "react"
import axios from "axios"
import { ArrowUpward, ArrowDownward, Edit, Delete } from "@mui/icons-material"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material"
import AddSupplierModal from "../../components/HRM/AddSupplierModal" // এই মডালগুলো আপাতত সাপ্লায়ারের জন্য
import EditSupplierModal from "../../components/HRM/EditSupplierModal" // এই মডালগুলো আপাতত সাপ্লায়ারের জন্য
import AddSupplierGroupModal from "../../components/HRM/AddSupplierGroupModal" // এই মডালগুলো আপাতত সাপ্লায়ারের জন্য
import ConfirmationDialog from "../../components/Common/ConfirmationDialog"
import { useOutletContext } from "react-router-dom"
import BaseURL from "../../api/AppURL";

const EmployeeManage = () => {
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const [downloading, setDownloading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null)
  const [addEmployeeGroupModalOpen, setAddEmployeeGroupModalOpen] = useState(false)

  // Confirmation Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [employeeToDeleteId, setEmployeeToDeleteId] = useState(null)

  const { showSnackbar } = useOutletContext()

  const fetchEmployees = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      // আপাতত সাপ্লায়ার API ব্যবহার করা হচ্ছে
      const response = await axios.get(`${BaseURL}/all/supplier`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      const fetchedData = Array.isArray(response.data.suppliers) ? response.data.suppliers : []
      setEmployees(fetchedData)
      console.log("Employees (using supplier API) fetched successfully. Total items:", fetchedData.length)
    } catch (err) {
      console.error("Error fetching Employees (using supplier API):", err)
      setError("Failed to load Employees. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEmployees()
  }, [])

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleEntriesChange = (event) => {
    setEntriesPerPage(event.target.value)
    setCurrentPage(1)
  }

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const sortedEmployees = [...employees].sort((a, b) => {
    if (sortConfig.key === null) return 0

    let aValue = a[sortConfig.key]
    let bValue = b[sortConfig.key]

    if (sortConfig.key === "group") {
      aValue = a.group?.name || ""
      bValue = b.group?.name || ""
    }

    if (aValue < bValue) {
      return sortConfig.direction === "ascending" ? -1 : 1
    }
    if (aValue > bValue) {
      return sortConfig.direction === "ascending" ? 1 : -1
    }
    return 0
  })

  const filteredEmployees = sortedEmployees.filter((employee) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      String(employee.name).toLowerCase().includes(searchLower) ||
      String(employee.mobile_no).toLowerCase().includes(searchLower) ||
      String(employee.address).toLowerCase().includes(searchLower) ||
      String(employee.group?.name || "")
        .toLowerCase()
        .includes(searchLower)
    )
  })

  const indexOfLastEmployee = currentPage * entriesPerPage
  const indexOfFirstEmployee = indexOfLastEmployee - entriesPerPage
  const displayedEmployees = filteredEmployees.slice(indexOfFirstEmployee, indexOfLastEmployee)
  const totalPages = Math.ceil(filteredEmployees.length / entriesPerPage)

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction === "ascending" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const totalEntries = filteredEmployees.length
  const startEntry = totalEntries === 0 ? 0 : indexOfFirstEmployee + 1
  const endEntry = Math.min(indexOfLastEmployee, totalEntries)

  const handleDownloadPdf = async () => {
    setDownloading(true)
    try {
      const token = localStorage.getItem("auth_token")
      // আপাতত সাপ্লায়ার API ব্যবহার করা হচ্ছে
      const response = await axios.get(`${BaseURL}/all/supplier/report`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
        responseType: "blob",
      })

      const contentDisposition = response.headers["content-disposition"]
      console.log("Content-Disposition Header:", contentDisposition)
      let filename = "employees_report.pdf"
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1]
        } else {
          const unquotedFilenameMatch = contentDisposition.match(/filename=([^;]+)/)
          if (unquotedFilenameMatch && unquotedFilenameMatch[1]) {
            filename = unquotedFilenameMatch[1].trim()
          }
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSnackbar("PDF report downloaded successfully!", "success")
    } catch (err) {
      console.error("Error downloading PDF:", err)
      showSnackbar("Failed to download PDF report. Please try again.", "error")
    } finally {
      setDownloading(false)
    }
  }

  const renderPageNumbers = () => {
    const pageNumbers = []
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={currentPage === i ? "contained" : "outlined"}
          color={currentPage === i ? "primary" : "inherit"}
          onClick={() => setCurrentPage(i)}
          sx={{ minWidth: 40, px: 1 }}
        >
          {i}
        </Button>,
      )
    }
    return pageNumbers
  }

  const handleAddEmployeeClick = () => {
    setAddModalOpen(true)
  }

  const handleAddModalClose = () => {
    setAddModalOpen(false)
  }

  const handleEmployeeAddedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchEmployees()
  }

  const handleEditEmployeeClick = (employeeId) => {
    setCurrentEmployeeId(employeeId)
    setEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setEditModalOpen(false)
    setCurrentEmployeeId(null)
  }

  const handleEmployeeUpdatedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchEmployees()
  }

  const handleDeleteEmployeeClick = (employeeId) => {
    setEmployeeToDeleteId(employeeId)
    setConfirmDialogOpen(true)
  }

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
    setEmployeeToDeleteId(null)
  }

  const handleConfirmEmployeeDelete = async () => {
    if (!employeeToDeleteId) return

    setConfirmDialogOpen(false)
    console.log("Attempting to delete Employee with ID:", employeeToDeleteId)

    try {
      const token = localStorage.getItem("auth_token")
      // আপাতত সাপ্লায়ার API ব্যবহার করা হচ্ছে
      const response = await axios.delete(`${BaseURL}/delete/supplier/${employeeToDeleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      console.log("DELETE API Response:", response.data)

      const isSuccess = response.data.success === true || response.data.message?.includes("successfully")

      if (isSuccess) {
        showSnackbar(response.data.message || "Employee deleted successfully!", "success")
        console.log("Employee deleted successfully. Optimistically updating UI...")

        setEmployees((prevEmployees) => {
          const updatedEmployees = prevEmployees.filter((e) => {
            const isMatch = String(e.id) === String(employeeToDeleteId)
            console.log(
              `Comparing e.id: ${e.id} (type: ${typeof e.id}) with employeeToDeleteId: ${employeeToDeleteId} (type: ${typeof employeeToDeleteId}). Match: ${isMatch}`,
            )
            return !isMatch
          })
          console.log("Employees after optimistic update. New count:", updatedEmployees.length)
          return updatedEmployees
        })

        await fetchEmployees()
        console.log("Table re-synced with backend after deletion.")
      } else {
        showSnackbar(response.data.message || "Failed to delete Employee.", "error")
        console.log("Failed to delete Employee (API reported failure or unexpected response):", response.data.message)
      }
    } catch (err) {
      console.error("Error deleting Employee:", err)
      showSnackbar("An error occurred while deleting the Employee.", "error")
    } finally {
      setEmployeeToDeleteId(null)
    }
  }

  const handleAddEmployeeGroupClick = () => {
    setAddEmployeeGroupModalOpen(true)
  }

  const handleAddEmployeeGroupModalClose = () => {
    setAddEmployeeGroupModalOpen(false)
  }

  const handleEmployeeGroupAddedSuccess = (message) => {
    showSnackbar(message, "success")
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Action Buttons Section */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={handleAddEmployeeClick}>
              Add Employee
            </Button>
            <Button variant="outlined" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              All Employees
            </Button>
            <Button variant="contained" onClick={handleAddEmployeeGroupClick}>
              Add Employee Group
            </Button>
          </Box>

          {/* Search and Entries Per Page Section */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={entriesPerPage}
                    onChange={handleEntriesChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Entries per page" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2">entries</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                sx={{ ml: { sm: "auto" } }}
              />
            </Grid>
          </Grid>

          {/* Table Section */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Box sx={{ overflowX: "auto", minWidth: 650 }}>
                <Table aria-label="employee table">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "5%", borderRight: "1px solid #e0e0e0" }}>
                        Sl
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "25%", borderRight: "1px solid #e0e0e0" }}>
                        <Button onClick={() => requestSort("name")} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
                          Name {getSortIcon("name")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", borderRight: "1px solid #e0e0e0" }}>
                        <Button onClick={() => requestSort("group")} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
                          Group {getSortIcon("group")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", borderRight: "1px solid #e0e0e0" }}>
                        <Button
                          onClick={() => requestSort("mobile_no")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        >
                          Mobile No {getSortIcon("mobile_no")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%", borderRight: "1px solid #e0e0e0" }}>
                        <Button
                          onClick={() => requestSort("address")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        >
                          Address {getSortIcon("address")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "10%" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedEmployees.length > 0 ? (
                      displayedEmployees.map((employee, index) => (
                        <TableRow key={employee.id || index}>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                            {indexOfFirstEmployee + index + 1}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{employee.name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{employee.group?.name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{employee.mobile_no}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{employee.address}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEditEmployeeClick(employee.id)}
                              >
                                <Edit fontSize="small" />
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteEmployeeClick(employee.id)}
                              >
                                <Delete fontSize="small" />
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                          No Employees found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}

          {/* Pagination Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handlePreviousPage} disabled={currentPage === 1}>
                Prev
              </Button>
              {renderPageNumbers()}
              <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Add Employee Modal (currently using AddSupplierModal) */}
      <AddSupplierModal open={addModalOpen} onClose={handleAddModalClose} onSaveSuccess={handleEmployeeAddedSuccess} />

      {/* Edit Employee Modal (currently using EditSupplierModal) */}
      {editModalOpen && (
        <EditSupplierModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onUpdateSuccess={handleEmployeeUpdatedSuccess}
          supplierId={currentEmployeeId}
        />
      )}

      {/* Add Employee Group Modal (currently using AddSupplierGroupModal) */}
      <AddSupplierGroupModal
        open={addEmployeeGroupModalOpen}
        onClose={handleAddEmployeeGroupModalClose}
        onSaveSuccess={handleEmployeeGroupAddedSuccess}
      />

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmEmployeeDelete}
        title="Are you sure?"
        message="Delete This Data?"
        confirmText="Yes, delete it!"
        cancelText="Cancel"
      />
    </Box>
  )
}

export default EmployeeManage
